import { Button, Form } from '@heroui/react'
import { AppRoutesList } from '@qse/constants'
import { HeroInput } from '@qse/ui'
import { ArrowLeft } from 'lucide-react'
import React, { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { useConfirmRegister } from '../logic/use-confirm-register'

// Validation function for confirmation code
const validateConfirmationCode = (value: string): string | undefined => {
  if (!value) {
    return 'Confirmation code is required'
  }
  if (!/^\d{6}$/.test(value)) {
    return 'Please enter a valid 6-digit code'
  }
  if (value.length !== 6) {
    return 'Code must be exactly 6 digits'
  }

  return undefined
}

const ConfirmEmail = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { handleConfirmRegister, handleResendCode, isResending } = useConfirmRegister()

  // Get email from navigation state or URL params
  const email = location.state?.email || ''

  const [count, setCount] = React.useState(0)
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const [confirmationCode, setConfirmationCode] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  React.useEffect(() => {
    if (count > 0) {
      timerRef.current = setTimeout(() => setCount(count - 1), 1000)
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [count])

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsLoading(true)

    if (!email) {
      toast.error('Email not found. Please try registering again.')
      navigate(AppRoutesList.register, { replace: true })
      setIsLoading(false)

      return
    }

    await handleConfirmRegister(email, confirmationCode, {
      setSubmitting: setIsLoading,
      onSuccess: async () => {
        toast.success('Email verified successfully! Please log in with your credentials.')
        navigate(AppRoutesList.login, { replace: true })
      },
    })
  }

  const onResendCode = async () => {
    setCount(30)
    await handleResendCode(email)
  }

  return (
    <div className="h-full">
      <div className="sticky top-0 flex lg:justify-end justify-between z-50 w-full px-4 py-4 dark:bg-[#000] bg-white">
        <Button
          as={Link}
          className="font-medium"
          startContent={<ArrowLeft size={16} />}
          to="/auth/login"
          variant="light"
        >
          Back
        </Button>
      </div>
      <div className="flex items-center h-[calc(100vh-72px)] w-full lg:pl-[110px] lg:max-w-full max-w-[440px] mx-auto">
        <div className="lg:w-[440px] w-full p-8 lg:p-0 flex flex-col gap-11">
          <div className="flex flex-col gap-2">
            <h1 className="text-[28px] font-medium">Confirm your email</h1>
            <p className="text-[15px]">
              Please enter the confirmation code sent to <strong>{email}</strong>
            </p>
          </div>
          <Form className="w-full" validationBehavior="native" onSubmit={handleSubmit}>
            <div className="w-full">
              <div className="flex flex-col gap-[30px]">
                <HeroInput
                  isRequired
                  control="text"
                  label="Confirmation Code"
                  name="confirmationCode"
                  placeholder="Enter 6-digit code"
                  radius="sm"
                  size="md"
                  validate={validateConfirmationCode}
                  value={confirmationCode}
                  variant="bordered"
                  onChange={setConfirmationCode}
                />
                <div className="mt-2">
                  <Button
                    className="w-full mb-5 font-medium"
                    color="primary"
                    isLoading={isLoading}
                    radius="sm"
                    type="submit"
                  >
                    Confirm Email
                  </Button>
                  <Button
                    className="w-full font-medium relative"
                    isDisabled={!email || isResending || count > 0}
                    isLoading={isResending}
                    radius="sm"
                    type="button"
                    variant="flat"
                    style={{
                      opacity: count > 0 ? 0.7 : 1,
                      cursor: count > 0 ? 'not-allowed' : 'pointer',
                    }}
                    onClick={onResendCode}
                  >
                    <div className="flex items-center gap-2 h-full justify-center">
                      Resend Code
                      {count > 0 && (
                        <span className="text-xs text-gray-400 font-normal">{`(${count}s)`}</span>
                      )}
                    </div>
                  </Button>
                </div>
              </div>
            </div>
          </Form>
        </div>
      </div>
    </div>
  )
}

export default ConfirmEmail
